/*
 * Autoplay only the second Shopify-hosted video
 * in the main product-page media gallery.
 *
 * Works on desktop and mobile.
 * Does not affect product cards, recommendations,
 * quick-add modals, external videos or 3D models.
 */

(() => {
  if (window.__mainProductVideoAutoplayInstalled) return;
  window.__mainProductVideoAutoplayInstalled = true;

  const desktopMediaQuery = window.matchMedia('(min-width: 750px)');

  function getMainProductGallery() {
    return document.querySelector('main media-gallery');
  }

  function getSecondMediaContainer(gallery) {
    if (!gallery) return null;

    /*
     * In grid presentation, Horizon renders:
     * - a slideshow for mobile
     * - a separate media grid for desktop
     */
    if (
      desktopMediaQuery.matches &&
      gallery.dataset.presentation === 'grid'
    ) {
      const gridItems = gallery.querySelectorAll(
        ':scope > .media-gallery__grid > li'
      );

      return gridItems[1] || null;
    }

    const slideshow = gallery.querySelector(
      ':scope > slideshow-component'
    );

    if (!slideshow) return null;

    const slides = slideshow.querySelectorAll(
      'slideshow-slide'
    );

    return slides[1] || null;
  }

  function loadVideo(container) {
    if (!container) return null;

    /*
     * Ignore images, external YouTube/Vimeo media and 3D models.
     */
    if (
      !container.classList.contains(
        'product-media-container--video'
      )
    ) {
      return null;
    }

    const deferredMedia = container.querySelector(
      'deferred-media'
    );

    if (!deferredMedia) return null;

    let video = deferredMedia.querySelector('video');

    if (!video) {
      const template = deferredMedia.querySelector('template');

      if (!template) return null;

      /*
       * Clone the template without deleting Horizon's original markup.
       */
      deferredMedia.appendChild(
        template.content.cloneNode(true)
      );

      deferredMedia.setAttribute('loaded', 'true');

      video = deferredMedia.querySelector('video');
    }

    if (!video) return null;

    video.muted = true;
    video.defaultMuted = true;
    video.loop = true;
    video.playsInline = true;
    video.autoplay = true;

    video.setAttribute('muted', '');
    video.setAttribute('loop', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('autoplay', '');

    const posterButton = deferredMedia.querySelector(
      '.deferred-media__poster-button'
    );

    if (posterButton) {
      posterButton.hidden = true;
    }

    return video;
  }

  function stopOtherProductVideos(gallery, activeContainer) {
    if (!gallery) return;

    gallery
      .querySelectorAll(
        '.product-media-container--video video'
      )
      .forEach(video => {
        if (activeContainer?.contains(video)) return;

        video.pause();
      });
  }

  async function initialiseProductVideo() {
    const gallery = getMainProductGallery();
    const secondMedia = getSecondMediaContainer(gallery);

    if (!gallery || !secondMedia) return;

    stopOtherProductVideos(gallery, secondMedia);

    const video = loadVideo(secondMedia);
    if (!video) return;

    try {
      await video.play();
    } catch (error) {
      console.warn(
        'Main product video autoplay was blocked:',
        error
      );
    }
  }

  function initialiseAfterRender() {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        initialiseProductVideo();
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initialiseAfterRender,
      { once: true }
    );
  } else {
    initialiseAfterRender();
  }

  /*
   * Select the correct desktop/mobile copy when crossing
   * Horizon's 750px breakpoint.
   */
  desktopMediaQuery.addEventListener(
    'change',
    initialiseAfterRender
  );

  /*
   * Reinitialise when Horizon replaces product-page content.
   */
  const observer = new MutationObserver(mutations => {
    const productGalleryAdded = mutations.some(mutation =>
      Array.from(mutation.addedNodes).some(node => {
        if (!(node instanceof Element)) return false;

        return (
          node.matches('media-gallery') ||
          node.querySelector('media-gallery')
        );
      })
    );

    if (productGalleryAdded) {
      initialiseAfterRender();
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      initialiseAfterRender();
    }
  });
})();